/**
 * Planner / timeboxing (spec §9.6): `planner[YYYY-MM-DD]` is an ordered list of `{taskId, start?, minutes?}`.
 * `start` is "HH:MM" local; an item without a start sits in the day's unscheduled strip.
 */
import { LoroMovableList, type LoroDoc } from "loro-crdt";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";

export interface PlanItem {
  taskId: string;
  start?: string;
  minutes?: number;
}

export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dayList(doc: LoroDoc, day: string, create: boolean): LoroMovableList<PlanItem> | undefined {
  const planner = doc.getMap(C.planner);
  const cur = planner.get(day) as unknown;
  if (cur instanceof LoroMovableList) return cur as LoroMovableList<PlanItem>;
  return create
    ? (planner.setContainer(day, new LoroMovableList()) as unknown as LoroMovableList<PlanItem>)
    : undefined;
}

export function readDay(doc: LoroDoc, day: string): PlanItem[] {
  const v = (doc.getMap(C.planner).toJSON() as Record<string, PlanItem[]>)[day];
  return Array.isArray(v) ? v.filter((i) => i && typeof i === "object" && i.taskId) : [];
}

export function readPlanner(doc: LoroDoc): Record<string, PlanItem[]> {
  const all = doc.getMap(C.planner).toJSON() as Record<string, PlanItem[]>;
  return Object.fromEntries(Object.entries(all).filter(([, v]) => Array.isArray(v)));
}

/** Plan a task on a day (moves it if it was already planned that day). */
export function planTask(doc: LoroDoc, day: string, item: PlanItem): void {
  const list = dayList(doc, day, true)!;
  const arr = list.toArray() as PlanItem[];
  const i = arr.findIndex((x) => x.taskId === item.taskId);
  const clean: PlanItem = {
    taskId: item.taskId,
    ...(item.start ? { start: item.start } : {}),
    ...(item.minutes ? { minutes: item.minutes } : {}),
  };
  if (i >= 0) list.set(i, clean);
  else list.push(clean);
  doc.commit({ message: commitMessage({ op: "planTask" }) });
}

export function unplanTask(doc: LoroDoc, day: string, taskId: string): void {
  const list = dayList(doc, day, false);
  if (!list) return;
  const i = (list.toArray() as PlanItem[]).findIndex((x) => x.taskId === taskId);
  if (i < 0) return;
  list.delete(i, 1);
  doc.commit({ message: commitMessage({ op: "unplanTask" }) });
}

/** Move a planned task to another day (carry-over in the daily summary uses this). */
export function movePlan(doc: LoroDoc, fromDay: string, toDay: string, taskId: string, start?: string): void {
  const from = dayList(doc, fromDay, false);
  const arr = (from?.toArray() as PlanItem[] | undefined) ?? [];
  const i = arr.findIndex((x) => x.taskId === taskId);
  const item = i >= 0 ? arr[i] : { taskId };
  if (from && i >= 0) from.delete(i, 1);
  const to = dayList(doc, toDay, true)!;
  const j = (to.toArray() as PlanItem[]).findIndex((x) => x.taskId === taskId);
  const next: PlanItem = {
    taskId,
    ...((start ?? item.start) ? { start: start ?? item.start } : {}),
    ...(item.minutes ? { minutes: item.minutes } : {}),
  };
  if (j >= 0) to.set(j, next);
  else to.push(next);
  doc.commit({ message: commitMessage({ op: "movePlan" }) });
}

/** What a dragged planner item carries: the task, and the day it came from when it was already planned. */
export const DRAG_TASK = "text/drang-task";
export const DRAG_FROM = "text/drang-from-day";

export interface Dragged {
  id: string;
  /** the day it was planned on, when it was dragged out of the grid */
  from: string;
}

/** Read what is dragged while the drop event lasts (the data is gone once the handler returns). */
export function dragged(data: DataTransfer): Dragged {
  return { id: data.getData(DRAG_TASK), from: data.getData(DRAG_FROM) };
}

/** Mark a planned item as it starts being dragged, so a drop elsewhere can take it off its day. */
export function startDrag(data: DataTransfer, taskId: string, fromDay?: string): void {
  data.setData(DRAG_TASK, taskId);
  if (fromDay) data.setData(DRAG_FROM, fromDay);
}

/**
 * Drop a dragged task on `day` at `start`: one already planned elsewhere leaves that day and keeps its length (an
 * item is planned on one day at a time when it is moved by dragging).
 */
export function dropPlan(doc: LoroDoc, d: Dragged, day: string, start: string, minutes = 60): void {
  if (!d.id) return;
  const prev = d.from ? readDay(doc, d.from).find((p) => p.taskId === d.id) : undefined;
  if (d.from && d.from !== day) unplanTask(doc, d.from, d.id);
  // a block keeps its length when moved; a newly dropped task gets `minutes` (its estimate, else an hour)
  planTask(doc, day, { taskId: d.id, start, minutes: prev?.minutes ?? minutes });
}

/** Dropped back on a tray ("Not planned"): an item that came from a day leaves it; a tray item stays as it was. */
export function dropUnplan(doc: LoroDoc, d: Dragged): void {
  if (d.id && d.from) unplanTask(doc, d.from, d.id);
}

export interface PlannedSlot extends PlanItem {
  day: string;
}

/** Where a task is planned, earliest first: every day it is on, with its start and length when set. */
export function plansOf(planner: Record<string, PlanItem[]>, taskId: string): PlannedSlot[] {
  return Object.entries(planner)
    .flatMap(([day, items]) => items.filter((p) => p.taskId === taskId).map((p) => ({ ...p, day })))
    .sort((a, b) => (a.day + (a.start ?? "")).localeCompare(b.day + (b.start ?? "")));
}

/** "10:00–11:30": the slot's start and end (its length, an hour when none is set). */
export function slotTime(slot: PlannedSlot): string | null {
  if (!slot.start) return null;
  const [h, m] = slot.start.split(":").map(Number);
  const end = (h ?? 0) * 60 + (m ?? 0) + (slot.minutes ?? 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${slot.start}–${pad(Math.floor(end / 60) % 24)}:${pad(end % 60)}`;
}
